import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded'
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded'
import { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import API from '../../../services/API'
import stepCatalogue from './stepCatalogue'
import { UserContext } from '../../../App'
import NotAuthorised from '../../../components/NotAuthorised'

const StepRow = ({ step, onToggleSkip }) => {
  const catalogue = stepCatalogue[step.key]
  if (!catalogue) return null

  const Icon = catalogue.icon
  const done = step.complete

  return (
    <Paper variant="outlined" sx={{ opacity: done || step.skipped ? 0.7 : 1 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        gap={2}
        p={2}
      >
        {done ? (
          <CheckCircleRoundedIcon color="success" />
        ) : (
          <RadioButtonUncheckedRoundedIcon color="disabled" />
        )}
        <Icon color={done ? 'disabled' : 'primary'} />
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              fontWeight={600}
              sx={{ textDecoration: done ? 'line-through' : 'none' }}
            >
              {catalogue.title}
            </Typography>
            {step.skipped && <Chip label="Skipped" size="small" />}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {catalogue.description}
          </Typography>
        </Box>
        {!done && (
          <Stack direction="row" gap={1} flexShrink={0}>
            <Button size="small" onClick={() => onToggleSkip(step)}>
              {step.skipped ? 'Unskip' : 'Skip'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              component={Link}
              to={catalogue.href}
            >
              {catalogue.action}
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}

const GettingStarted = () => {
  const { user } = useContext(UserContext)
  const [status, setStatus] = useState(null)

  useEffect(() => {
    if (!user?.security?.isSiteAdmin) return
    API.get('/onboarding/status').then(
      (res) => res.status === 200 && setStatus(res.data),
    )
  }, [user?.security?.isSiteAdmin])

  if (!user?.security?.isSiteAdmin) return <NotAuthorised />
  if (!status) return null

  const toggleSkip = (step) =>
    API.post(`/onboarding/steps/${step.key}/skip`, {
      skipped: !step.skipped,
    }).then((res) => res.status === 200 && setStatus(res.data))

  const allDone = status.steps.every((s) => s.complete || s.skipped)
  const progress = (status.completedSteps / status.totalSteps) * 100

  return (
    <Stack gap={2} maxWidth={900} mx="auto" width="100%">
      <Box>
        <Typography variant="h5" fontWeight={600}>
          Getting Started
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Work through these steps to get your organisation fully up and
          running. Steps tick themselves off as you add data — nothing here is
          compulsory, and you can skip anything that doesn't apply.
        </Typography>
      </Box>

      <Box>
        <Box display="flex" justifyContent="space-between" mb={0.5}>
          <Typography variant="caption">
            {status.completedSteps} of {status.totalSteps} complete
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          color={allDone ? 'success' : 'primary'}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>

      {allDone && (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <CelebrationRoundedIcon color="success" sx={{ fontSize: 48 }} />
          <Typography variant="h6" fontWeight={600}>
            You're all set up!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your organisation is ready to go. You can revisit any of this under
            Site Configuration.
          </Typography>
        </Paper>
      )}

      {status.steps.map((step) => (
        <StepRow key={step.key} step={step} onToggleSkip={toggleSkip} />
      ))}
    </Stack>
  )
}

export default GettingStarted
